import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import LimitReachedModal from "../LimitReachedModal";
import AIImagePanel from "./AIImagePanel";
import AIEditPanel from "./AIEditPanel";
import AssemblePreviewModal from "./AssemblePreviewModal";
import BlurbImportModal from "./BlurbImportModal";
import ShareModal from "./ShareModal";
import ResizeKDPModal from "./ResizeKDPModal";
import { pushRecentColor } from "./ColorPicker";
import { TRIM_SIZES } from "./BookSetupModal";
import EditorTopBar from "./editor/EditorTopBar";
import LeftToolbar from "./editor/LeftToolbar";
import PropertiesPanel from "./editor/PropertiesPanel";
import {
  DISPLAY_SCALE, BLEED as DEFAULT_BLEED, SAFE, getBleed,
  snapshotProps, isInternalObj,
} from "./editor/constants";

// keep the linter quiet — used as a fallback only when binding is missing
void DEFAULT_BLEED;

function getPanelDimsInches(panel, setup) {
  const trim = TRIM_SIZES.find((t) => t.value === setup.trim_size) || TRIM_SIZES[0];
  if (panel === "front" || panel === "back") return { trimW: trim.w, trimH: trim.h };
  return { trimW: setup.spine_width_inches || 0.25, trimH: trim.h };
}

const CoverEditor = ({ setup, project, onResize }) => {
  // Per-edge bleed is binding-dependent: paperback 0.125", hardcover 0.51".
  // Everything else in this file refers to `BLEED` (shadowed by this const)
  // so the canvas, guides, exporters, etc. all scale automatically.
  const BLEED = getBleed(setup.binding_type);
  const navigate = useNavigate();
  const { profile } = useAuth();

  // When the calculated spine width is below KDP's minimum for spine text
  // (0.0625" — roughly < 80 pages on cream), there's effectively no spine.
  // KDP wraps the cover at the edge and forbids spine text. We auto-hide
  // the spine panel and skip it in the full-wrap exporter.
  const hasSpine = (setup.spine_width_inches || 0) >= 0.0625;

  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);
  const guidesRef = useRef([]);
  const activeObjRef = useRef(null);
  const isUpdatingRef = useRef(false);

  const [panel, setPanel] = useState(hasSpine ? "front" : "front");
  const [activeProps, setActiveProps] = useState(null);
  const [layers, setLayers] = useState([]);
  const [showAI, setShowAI] = useState(false);
  const [showAIEdit, setShowAIEdit] = useState(false);
  // KDP template overlay (bleed/trim/safe/barcode/spine zones with labels).
  // Persisted to localStorage so the user's preference survives reloads.
  const [showTemplate, setShowTemplate] = useState(() => {
    try {
      const v = window.localStorage.getItem("pk:show-template");
      return v == null ? true : v === "true";
    } catch (_) { return true; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("pk:show-template", String(showTemplate)); } catch (_) {}
  }, [showTemplate]);
  // Per-image-layer edit history kept in-session. Keyed by the layer's
  // __pkEditId (a uuid we stamp the first time it's edited). Each entry
  // is { src, prompt, ts } where src is the data URL the layer had
  // BEFORE that prompt was applied; ts is a millisecond timestamp.
  const editHistory = useRef({});
  const [historyVersion, setHistoryVersion] = useState(0);
  const [showBlurb, setShowBlurb] = useState(false);
  const [panelBg, setPanelBg] = useState({ front: "#FFFFFF", spine: "#FFFFFF", back: "#FFFFFF" });
  const bgColor = panelBg[panel];
  const setBgColor = (c) => {
    setPanelBg((p) => ({ ...p, [panel]: c }));
    dirtyRef.current = true;
  };
  const [spineMatchFront, setSpineMatchFront] = useState(false);
  const [sampling, setSampling] = useState(false);
  const [eyedropping, setEyedropping] = useState(false);
  const [backOverlay, setBackOverlay] = useState({ opacity: 0, color: "#000000" });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(project?.id || null);
  const [lastSavedAt, setLastSavedAt] = useState(project ? new Date() : null);
  const [fabricReady, setFabricReady] = useState(typeof window !== "undefined" && !!window.fabric);
  const [showPreview, setShowPreview] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showResize, setShowResize] = useState(false);
  const [shareToken, setShareToken] = useState(project?.share_token || "");
  const [exporting, setExporting] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [foundLostCount, setFoundLostCount] = useState(null);
  const dirtyRef = useRef(false);
  const savedIdRef = useRef(project?.id || null);
  const handleSaveRef = useRef(null);

  const panelStates = useRef({ front: null, spine: null, back: null });
  const undoStack = useRef({ front: [], spine: [], back: [] });
  const redoStack = useRef({ front: [], spine: [], back: [] });
  const snapGuidesRef = useRef([]);

  /* --------------------------- Fabric ready --------------------------- */
  useEffect(() => {
    if (window.fabric) { setFabricReady(true); return; }
    const interval = setInterval(() => {
      if (window.fabric) { setFabricReady(true); clearInterval(interval); }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Redirect away from the spine panel if it's been disabled (e.g. on
  // a < 0.0625" spine, where KDP doesn't permit a printed spine).
  useEffect(() => {
    if (!hasSpine && panel === "spine") setPanel("front");
  }, [hasSpine, panel]);

  /* --------------------------- Init / panel switch --------------------------- */
  // The cleanup runs BEFORE the new effect, with the OLD `panel` value in
  // closure — which is exactly what we need to save the outgoing panel's
  // canvas state to the right slot in `panelStates`.
  useEffect(() => {
    if (!fabricReady || !canvasElRef.current) return;
    return () => {
      if (fabricRef.current) {
        try {
          panelStates.current[panel] = fabricRef.current.toJSON([
            "excludeFromExport", "lockMovementX", "lockMovementY", "lockScalingX", "lockScalingY",
            "selectable", "name", "hidden",
          ]);
        } catch (_) {}
        fabricRef.current.dispose();
        fabricRef.current = null;
      }
      activeObjRef.current = null;
      setActiveProps(null);
    };
  }, [fabricReady, panel]);

  useEffect(() => {
    if (!fabricReady || !canvasElRef.current || fabricRef.current) return;
    const fabric = window.fabric;
    const dims = getPanelDimsInches(panel, setup);
    // Spine zoom: magnify spine panel so narrow spines are editable
    const SPINE_MULT = (panel === "spine")
      ? Math.max(8, Math.min(12, 2.0 / (dims.trimW || 0.5))) * DISPLAY_SCALE
      : DISPLAY_SCALE;
    const widthPx = Math.round((dims.trimW + BLEED * 2) * SPINE_MULT);
    const heightPx = Math.round((dims.trimH + BLEED * 2) * SPINE_MULT);

    canvasElRef.current.width = widthPx;
    canvasElRef.current.height = heightPx;

    const c = new fabric.Canvas(canvasElRef.current, {
      backgroundColor: bgColor,
      preserveObjectStacking: true,
      selection: true,
    });
    c.setDimensions({ width: widthPx, height: heightPx });
    fabricRef.current = c;

    const stash = panelStates.current[panel];
    const initialJSON = !stash && project ? (
      panel === "front" ? project.front_canvas_json
        : panel === "spine" ? project.spine_canvas_json
        : project.back_canvas_json
    ) : stash;

    const onReady = () => {
      drawGuides();
      // Detect existing back-cover overlay (so the slider stays in sync)
      if (panel === "back") {
        const existing = c.getObjects().find((o) => o.name === "_overlay");
        if (existing) {
          setBackOverlay({ opacity: existing.opacity || 0.4, color: existing.fill || "#000000" });
        } else {
          setBackOverlay({ opacity: 0, color: "#000000" });
        }
      }
      c.requestRenderAll();
      refreshLayers();
    };

    if (initialJSON) {
      c.loadFromJSON(initialJSON, () => {
        const bg = initialJSON.background || bgColor;
        c.backgroundColor = bg;
        if (initialJSON.background) {
          setPanelBg((p) => ({ ...p, [panel]: initialJSON.background }));
        }
        onReady();
      });
    } else {
      onReady();
    }

    const onSelect = () => {
      const obj = c.getActiveObject();
      activeObjRef.current = obj || null;
      setActiveProps(obj ? snapshotProps(obj) : null);
    };
    c.on("selection:created", onSelect);
    c.on("selection:updated", onSelect);
    c.on("selection:cleared", () => { activeObjRef.current = null; setActiveProps(null); });
    c.on("object:added", () => { refreshLayers(); pushUndo(); dirtyRef.current = true; });
    c.on("object:removed", () => { refreshLayers(); dirtyRef.current = true; });
    c.on("object:modified", () => {
      refreshLayers(); pushUndo(); dirtyRef.current = true;
      if (activeObjRef.current) setActiveProps(snapshotProps(activeObjRef.current));
    });
    c.on("object:moving", handleObjectMoving);
    c.on("mouse:up", () => { clearSnapGuides(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricReady, panel]);

  useEffect(() => {
    if (fabricRef.current) {
      fabricRef.current.backgroundColor = bgColor;
      fabricRef.current.requestRenderAll();
    }
  }, [bgColor]);

  /* --------------------------- Eyedropper (pick pixel from canvas) --------------------------- */
  useEffect(() => {
    const c = fabricRef.current;
    if (!c) return;
    if (!eyedropping) {
      c.defaultCursor = "default";
      c.hoverCursor = "move";
      c.selection = true;
      return;
    }
    // Enter eyedropper mode — disable selection, force crosshair cursor.
    c.defaultCursor = "crosshair";
    c.hoverCursor = "crosshair";
    c.selection = false;
    c.discardActiveObject();
    c.requestRenderAll();

    const onPick = (opt) => {
      try {
        const pointer = c.getPointer(opt.e, true);
        const lower = c.lowerCanvasEl;
        const ctx = lower.getContext("2d", { willReadFrequently: true });
        // Account for retina scaling (lowerCanvasEl may be at devicePixelRatio while
        // pointer is in CSS-pixel canvas units).
        const retina = (typeof c.getRetinaScaling === "function" ? c.getRetinaScaling() : 1) || 1;
        const x = Math.max(0, Math.min(lower.width - 1, Math.round(pointer.x * retina)));
        const y = Math.max(0, Math.min(lower.height - 1, Math.round(pointer.y * retina)));
        const data = ctx.getImageData(x, y, 1, 1).data;
        const hex = "#" + [data[0], data[1], data[2]]
          .map((n) => n.toString(16).padStart(2, "0"))
          .join("")
          .toUpperCase();
        setBgColor(hex);
        pushRecentColor(hex);
      } catch (err) {
        console.error("Eyedropper sample failed", err);
      } finally {
        setEyedropping(false);
      }
    };
    c.on("mouse:down", onPick);
    return () => {
      c.off("mouse:down", onPick);
      c.defaultCursor = "default";
      c.hoverCursor = "move";
      c.selection = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eyedropping, panel]);

  /* --------------------------- Back-cover dim overlay --------------------------- */
  useEffect(() => {
    if (panel !== "back") return;
    const c = fabricRef.current;
    const fabric = window.fabric;
    if (!c || !fabric) return;
    const existing = c.getObjects().find((o) => o.name === "_overlay");
    if ((backOverlay.opacity || 0) < 0.01) {
      if (existing) {
        c.remove(existing);
        c.requestRenderAll();
        dirtyRef.current = true;
      }
      return;
    }
    if (existing) {
      existing.set({ opacity: backOverlay.opacity, fill: backOverlay.color });
      c.requestRenderAll();
      dirtyRef.current = true;
      return;
    }
    const w = c.getWidth();
    const h = c.getHeight();
    const rect = new fabric.Rect({
      left: 0, top: 0, width: w, height: h,
      fill: backOverlay.color, opacity: backOverlay.opacity,
      selectable: false, evented: false, excludeFromExport: false,
      name: "_overlay", hoverCursor: "default",
    });
    c.add(rect);
    // Keep overlay just above background images, below text/shapes.
    c.sendToBack(rect);
    c.getObjects().forEach((o) => {
      if (o === rect) return;
      if (o.type === "image" || o.name === "ai-image" || o.name === "image") c.sendToBack(o);
    });
    guidesRef.current.forEach((g) => c.bringToFront(g));
    c.requestRenderAll();
    dirtyRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOverlay, panel]);

  /* --------------------------- Snap-to-centre --------------------------- */
  const SNAP_THRESHOLD = 6;
  const clearSnapGuides = () => {
    const c = fabricRef.current;
    if (!c) return;
    snapGuidesRef.current.forEach((g) => c.remove(g));
    snapGuidesRef.current = [];
    c.requestRenderAll();
  };
  const drawSnapLine = (kind) => {
    const c = fabricRef.current;
    const fabric = window.fabric;
    if (!c || !fabric) return;
    const w = c.getWidth();
    const h = c.getHeight();
    const opts = {
      stroke: "#3460D7", strokeWidth: 1, strokeDashArray: [4, 4],
      selectable: false, evented: false, excludeFromExport: true,
      hoverCursor: "default", name: "_snap",
    };
    const line = kind === "v"
      ? new fabric.Line([w / 2, 0, w / 2, h], opts)
      : new fabric.Line([0, h / 2, w, h / 2], opts);
    c.add(line);
    c.bringToFront(line);
    snapGuidesRef.current.push(line);
  };
  const handleObjectMoving = (e) => {
    const c = fabricRef.current;
    if (!c) return;
    const obj = e.target;
    if (!obj || isInternalObj(obj)) return;
    const cx = c.getWidth() / 2;
    const cy = c.getHeight() / 2;
    const center = obj.getCenterPoint();
    clearSnapGuides();
    if (Math.abs(center.x - cx) < SNAP_THRESHOLD) {
      obj.setPositionByOrigin({ x: cx, y: center.y }, "center", "center");
      drawSnapLine("v");
    }
    if (Math.abs(center.y - cy) < SNAP_THRESHOLD) {
      const newCenter = obj.getCenterPoint();
      obj.setPositionByOrigin({ x: newCenter.x, y: cy }, "center", "center");
      drawSnapLine("h");
    }
    guidesRef.current.forEach((g) => c.bringToFront(g));
  };

  /* --------------------------- Find lost objects --------------------------- */
  const findLostObjects = () => {
    const c = fabricRef.current;
    if (!c) return;
    const w = c.getWidth();
    const h = c.getHeight();
    let count = 0;
    c.getObjects().forEach((o) => {
      if (isInternalObj(o)) return;
      const center = o.getCenterPoint();
      if (center.x < 0 || center.x > w || center.y < 0 || center.y > h) {
        o.setPositionByOrigin({ x: w / 2, y: h / 2 }, "center", "center");
        o.setCoords();
        count += 1;
      }
    });
    c.requestRenderAll();
    setFoundLostCount(count);
    setTimeout(() => setFoundLostCount(null), 2500);
  };

  /* --------------------------- Spine auto-match (live colour) --------------------------- */
  useEffect(() => {
    if (!spineMatchFront) return;
    setPanelBg((p) => ({ ...p, spine: p.front }));
  }, [spineMatchFront, panelBg.front]);

  /* --------------------------- Spine sample-from-front --------------------------- */
  const sampleFrontDominantColor = async () => {
    const fabric = window.fabric;
    if (!fabric) return;
    setSampling(true);
    try {
      const trim = TRIM_SIZES.find((t) => t.value === setup.trim_size) || TRIM_SIZES[0];
      const frontJSON = panelStates.current.front || project?.front_canvas_json;
      // If the front has no real objects (just a background colour plus
      // guides — which are excluded by toJSON), trust the React state for
      // the front bg colour. The snapshot in panelStates may be stale
      // because it was captured before the most recent swatch click was
      // flushed to the canvas. With no images/shapes there's nothing to
      // sample anyway — `panelBg.front` is the user's intent.
      const hasObjects = !!(frontJSON && Array.isArray(frontJSON.objects) && frontJSON.objects.length > 0);
      let hex = panelBg.front;
      if (hasObjects) {
        const w = Math.round((trim.w + BLEED * 2) * DISPLAY_SCALE);
        const h = Math.round((trim.h + BLEED * 2) * DISPLAY_SCALE);
        const el = document.createElement("canvas");
        el.width = w; el.height = h;
        const sc = new fabric.StaticCanvas(el, {
          width: w, height: h,
          backgroundColor: frontJSON.background || "#FFFFFF",
        });
        await new Promise((resolve) => {
          sc.loadFromJSON(frontJSON, () => {
            sc.getObjects().filter((o) => isInternalObj(o)).forEach((g) => sc.remove(g));
            sc.renderAll();
            // Wait one frame so any image-load completes painting.
            requestAnimationFrame(() => resolve());
          });
        });
        const ctx = el.getContext("2d");
        const sx = Math.floor(w * 0.2);
        const sy = Math.floor(h * 0.2);
        const sw = Math.floor(w * 0.6);
        const sh = Math.floor(h * 0.6);
        const data = ctx.getImageData(sx, sy, sw, sh).data;
        let r = 0, g = 0, b = 0, count = 0;
        for (let i = 0; i < data.length; i += 40) {
          if (data[i + 3] < 128) continue;
          r += data[i]; g += data[i + 1]; b += data[i + 2]; count += 1;
        }
        sc.dispose();
        if (count > 0) {
          r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count);
          hex = "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
        }
      }
      // Disable the live-match toggle so the sampled colour doesn't get
      // immediately overwritten when the user later changes the front bg.
      setSpineMatchFront(false);
      setPanelBg((p) => ({ ...p, spine: hex }));
      pushRecentColor(hex);
      dirtyRef.current = true;
    } finally {
      setSampling(false);
    }
  };

  /* --------------------------- Guides --------------------------- */
  const drawGuides = useCallback(() => {
    const c = fabricRef.current;
    if (!c) return;
    const fabric = window.fabric;
    guidesRef.current.forEach((g) => c.remove(g));
    guidesRef.current = [];
    if (!showTemplate) { c.requestRenderAll(); return; }

    const dims = getPanelDimsInches(panel, setup);
    const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
    const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
    const bleedPx = BLEED * DISPLAY_SCALE;
    const safePx = SAFE * DISPLAY_SCALE;

    const commonStatic = {
      selectable: false, evented: false, excludeFromExport: true,
      hoverCursor: "default", objectCaching: false,
    };

    // 1) BLEED STRIP — translucent red border around the trim. Mirrors
    //    KDP's "red zone" so the user can SEE the bleed area instead of
    //    inferring it from a dashed line.
    const bleedStrip = new fabric.Rect({
      left: 0, top: 0, width: widthPx, height: heightPx,
      fill: "rgba(215, 67, 67, 0.08)", stroke: undefined,
      name: "_guide_bleed_strip", ...commonStatic,
    });
    // Cut a transparent hole at the trim area by stacking a white rect on top.
    // (Simpler than using clipPaths — works in all Fabric versions.)
    const bleedHole = new fabric.Rect({
      left: bleedPx, top: bleedPx,
      width: widthPx - bleedPx * 2, height: heightPx - bleedPx * 2,
      fill: "rgba(255,255,255,0)", // transparent — just to mask the red where the trim sits
      stroke: undefined,
      name: "_guide_bleed_hole", ...commonStatic,
    });
    c.add(bleedStrip, bleedHole);
    guidesRef.current.push(bleedStrip, bleedHole);


    // 2) TRIM LINE — solid black: the actual cut edge of the book.
    const trimLine = new fabric.Rect({
      left: bleedPx, top: bleedPx,
      width: widthPx - bleedPx * 2, height: heightPx - bleedPx * 2,
      fill: "transparent", stroke: "#2C2C2A", strokeWidth: 1.2,
      name: "_guide_trim", ...commonStatic,
    });
    // 3) SAFE AREA — dashed blue: keep text/important art inside here.
    const safeRect = new fabric.Rect({
      left: bleedPx + safePx, top: bleedPx + safePx,
      width: widthPx - (bleedPx + safePx) * 2, height: heightPx - (bleedPx + safePx) * 2,
      fill: "transparent", stroke: "#3460D7", strokeDashArray: [5, 4], strokeWidth: 1,
      name: "_guide_safe", ...commonStatic,
    });
    c.add(trimLine, safeRect);
    guidesRef.current.push(trimLine, safeRect);

    // Helper: small label tag in a corner of the canvas.
    const tag = (text, x, y, opts = {}) => new fabric.Text(text, {
      left: x, top: y, fontSize: 10, fontFamily: "Inter",
      fill: opts.color || "#5F5E5A",
      backgroundColor: opts.bg || "rgba(255,255,255,0.85)",
      padding: 2,
      originX: opts.originX || "left", originY: opts.originY || "top",
      name: opts.name || "_guide_label", ...commonStatic,
    });
    // Three labels along the top of the canvas — bleed / trim / safe.
    const lblBleed = tag("BLEED – extend art here", 6, 4, { color: "#D74343", name: "_guide_label_bleed" });
    const lblTrim = tag("TRIM (cut line)", bleedPx + 6, bleedPx + 4, { color: "#2C2C2A", name: "_guide_label_trim" });
    const lblSafe = tag("SAFE – keep text inside", bleedPx + safePx + 6, bleedPx + safePx + 4, { color: "#3460D7", name: "_guide_label_safe" });
    c.add(lblBleed, lblTrim, lblSafe);
    guidesRef.current.push(lblBleed, lblTrim, lblSafe);

    // 4) SPINE-specific: "do not place text within 0.0625&quot; of either edge".
    //    Only meaningful when the user is on the spine panel.
    if (panel === "spine") {
      const inset = 0.0625 * DISPLAY_SCALE;
      const leftLine = new fabric.Line(
        [bleedPx + inset, bleedPx, bleedPx + inset, heightPx - bleedPx],
        { stroke: "#BA7517", strokeDashArray: [3, 3], strokeWidth: 1, name: "_guide_spine_left", ...commonStatic },
      );
      const rightLine = new fabric.Line(
        [widthPx - bleedPx - inset, bleedPx, widthPx - bleedPx - inset, heightPx - bleedPx],
        { stroke: "#BA7517", strokeDashArray: [3, 3], strokeWidth: 1, name: "_guide_spine_right", ...commonStatic },
      );
      c.add(leftLine, rightLine);
      guidesRef.current.push(leftLine, rightLine);
    }

    // 5) BACK COVER: KDP-reserved ISBN barcode placement zone.
    //    2.0" × 1.2" with 0.25" margin from trim edges, bottom-right.
    if (panel === "back") {
      const bw = 2.0 * DISPLAY_SCALE;
      const bh = 1.2 * DISPLAY_SCALE;
      const right = widthPx - bleedPx - 0.25 * DISPLAY_SCALE;
      const bottom = heightPx - bleedPx - 0.25 * DISPLAY_SCALE;
      const barcode = new fabric.Rect({
        left: right - bw, top: bottom - bh, width: bw, height: bh,
        fill: "rgba(120,120,118,0.18)", stroke: "#5F5E5A", strokeDashArray: [4, 4], strokeWidth: 1,
        name: "_guide_barcode", ...commonStatic,
      });
      const label = tag("KDP barcode zone — keep clear", right - bw / 2, bottom - bh / 2, {
        originX: "center", originY: "center",
        bg: "rgba(255,255,255,0.9)", name: "_guide_barcode_label",
      });
      c.add(barcode, label);
      guidesRef.current.push(barcode, label);
    }

    guidesRef.current.forEach((g) => c.bringToFront(g));
    c.requestRenderAll();
  }, [panel, setup, showTemplate, BLEED]);

  // Re-draw guides whenever the user toggles the template overlay.
  useEffect(() => {
    if (fabricRef.current) drawGuides();
  }, [showTemplate, drawGuides]);

  /* --------------------------- Layers panel --------------------------- */
  const refreshLayers = useCallback(() => {
    const c = fabricRef.current;
    if (!c) return;
    const objs = c.getObjects().filter((o) => !isInternalObj(o));
    setLayers(objs.map((o, i) => ({
      idx: i,
      obj: o,
      type: o.type,
      label: o.text ? `"${String(o.text).slice(0, 22)}"` : (o.name || `${o.type} ${i + 1}`),
      visible: o.visible !== false,
      locked: !!o.lockMovementX,
    })));
  }, []);

  /* --------------------------- Undo / Redo --------------------------- */
  const pushUndo = () => {
    if (isUpdatingRef.current) return;
    const c = fabricRef.current;
    if (!c) return;
    const json = c.toJSON([
      "excludeFromExport", "lockMovementX", "lockMovementY", "lockScalingX", "lockScalingY",
      "selectable", "name", "hidden",
    ]);
    undoStack.current[panel].push(JSON.stringify(json));
    if (undoStack.current[panel].length > 30) undoStack.current[panel].shift();
    redoStack.current[panel] = [];
  };
  const undo = () => {
    const stack = undoStack.current[panel];
    if (stack.length < 2) return;
    const c = fabricRef.current;
    const current = stack.pop();
    redoStack.current[panel].push(current);
    const prev = stack[stack.length - 1];
    if (!prev) return;
    isUpdatingRef.current = true;
    c.loadFromJSON(JSON.parse(prev), () => {
      drawGuides(); c.requestRenderAll(); refreshLayers();
      isUpdatingRef.current = false;
    });
  };
  const redo = () => {
    const stack = redoStack.current[panel];
    if (!stack.length) return;
    const c = fabricRef.current;
    const next = stack.pop();
    undoStack.current[panel].push(next);
    isUpdatingRef.current = true;
    c.loadFromJSON(JSON.parse(next), () => {
      drawGuides(); c.requestRenderAll(); refreshLayers();
      isUpdatingRef.current = false;
    });
  };

  /* --------------------------- Active object updates --------------------------- */
  const updateActive = (key, value) => {
    const c = fabricRef.current;
    const obj = activeObjRef.current || c?.getActiveObject();
    if (!obj || isInternalObj(obj)) return;
    obj.set({ [key]: value });
    obj.setCoords();
    c.requestRenderAll();
    setActiveProps(snapshotProps(obj));
    refreshLayers();
  };

  const updateActiveSize = (newW, newH) => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!obj) return;
    const baseW = obj.width || 1;
    const baseH = obj.height || 1;
    if (newW != null) obj.set({ scaleX: Math.max(0.05, newW / baseW) });
    if (newH != null) obj.set({ scaleY: Math.max(0.05, newH / baseH) });
    obj.setCoords();
    c.requestRenderAll();
    setActiveProps(snapshotProps(obj));
  };

  const updateActiveRadius = (newR) => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!obj) return;
    obj.set({ radius: newR, scaleX: 1, scaleY: 1 });
    obj.setCoords();
    c.requestRenderAll();
    setActiveProps(snapshotProps(obj));
  };

  const duplicateActive = () => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!obj || !obj.clone) return;
    obj.clone((clone) => {
      clone.set({ left: (obj.left || 0) + 20, top: (obj.top || 0) + 20, name: obj.name || obj.type });
      c.add(clone);
      c.setActiveObject(clone);
      activeObjRef.current = clone;
      setActiveProps(snapshotProps(clone));
      guidesRef.current.forEach((g) => c.bringToFront(g));
      c.requestRenderAll();
    }, ["name", "lockMovementX", "lockMovementY"]);
  };

  const deleteActive = () => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!obj) return;
    c.remove(obj);
    c.discardActiveObject();
    activeObjRef.current = null;
    setActiveProps(null);
    c.requestRenderAll();
  };

  const bringToFront = () => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!c || !obj) return;
    c.bringToFront(obj);
    guidesRef.current.forEach((g) => c.bringToFront(g));
    c.requestRenderAll();
  };
  const sendToBack = () => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!c || !obj) return;
    c.sendToBack(obj);
    c.requestRenderAll();
  };

  /* --------------------------- Keyboard shortcuts --------------------------- */
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") { e.preventDefault(); duplicateActive(); }
      else if (e.key === "Delete" || e.key === "Backspace") {
        if (activeObjRef.current && !isInternalObj(activeObjRef.current)) deleteActive();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel]);

  /* --------------------------- Tools --------------------------- */
  const addText = (variant = "custom") => {
    const fabric = window.fabric;
    const c = fabricRef.current;
    if (!c) return;
    const dims = getPanelDimsInches(panel, setup);
    const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
    const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
    let text = "Your text here";
    let fontSize = 36;
    let top = heightPx / 2;
    let fontFamily = "Outfit";
    let fontWeight = "normal";
    if (variant === "title") {
      text = setup.book_title || "BOOK TITLE";
      fontSize = panel === "spine" ? 22 : 56;
      top = heightPx * 0.32;
      fontFamily = "Cormorant Garamond";
      fontWeight = "500";
    } else if (variant === "subtitle") {
      text = setup.subtitle || "Subtitle";
      fontSize = panel === "spine" ? 14 : 24;
      top = heightPx * 0.46;
      fontFamily = "Outfit";
    } else if (variant === "author") {
      text = setup.author || "Author Name";
      fontSize = panel === "spine" ? 14 : 22;
      top = heightPx * 0.85;
      fontFamily = "Outfit";
    }
    const t = new fabric.Textbox(text, {
      left: widthPx / 2, top, fontSize, fontFamily, fontWeight,
      fill: "#2C2C2A",
      originX: "center", originY: "center",
      width: widthPx * 0.8, textAlign: "center", name: variant,
    });
    if (panel === "spine") t.set({ angle: 90 });
    c.add(t);
    c.setActiveObject(t);
    activeObjRef.current = t;
    setActiveProps(snapshotProps(t));
    c.requestRenderAll();
  };

  const placeBlurb = (text) => {
    const fabric = window.fabric;
    const c = fabricRef.current;
    if (!c) return;
    const dims = getPanelDimsInches(panel, setup);
    const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
    const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
    const t = new fabric.Textbox(text, {
      left: widthPx / 2, top: heightPx * 0.45,
      fontSize: 16, fontFamily: "Inter", fill: "#2C2C2A",
      originX: "center", originY: "center",
      width: widthPx * 0.78, textAlign: "left", lineHeight: 1.45, name: "blurb",
    });
    c.add(t);
    c.setActiveObject(t);
    activeObjRef.current = t;
    setActiveProps(snapshotProps(t));
    refreshLayers();
    c.requestRenderAll();
  };

  const addShape = (kind) => {
    const fabric = window.fabric;
    const c = fabricRef.current;
    if (!c) return;
    let obj;
    const opts = { left: 80, top: 80, fill: "#534AB7", stroke: "", strokeWidth: 0, name: kind };
    if (kind === "rect") obj = new fabric.Rect({ ...opts, width: 140, height: 90 });
    else if (kind === "circle") obj = new fabric.Circle({ ...opts, radius: 50 });
    else if (kind === "line") obj = new fabric.Line([20, 20, 220, 20], { left: 80, top: 200, stroke: "#534AB7", strokeWidth: 4, name: "line" });
    else if (kind === "arrow") {
      obj = new fabric.Path(
        "M 0 13 L 150 8 L 150 0 L 200 15 L 150 30 L 150 22 L 0 17 Z",
        { left: 80, top: 200, fill: "#534AB7", stroke: "", strokeWidth: 0, name: "arrow", objectCaching: false }
      );
    } else if (kind === "double-arrow") {
      obj = new fabric.Path(
        "M 0 15 L 50 0 L 50 8 L 200 8 L 200 0 L 250 15 L 200 30 L 200 22 L 50 22 L 50 30 Z",
        { left: 80, top: 200, fill: "#534AB7", stroke: "", strokeWidth: 0, name: "double-arrow", objectCaching: false }
      );
    } else return;
    c.add(obj);
    c.setActiveObject(obj);
    activeObjRef.current = obj;
    setActiveProps(snapshotProps(obj));
    c.requestRenderAll();
  };

  /**
   * getDominantColour — samples a downscaled version of an image element
   * and returns the most-frequent non-near-white/black pixel as a hex string.
   * Used to auto-fill the background when a cover image is uploaded.
   */
  const getDominantColour = (imgEl) => {
    try {
      const SIZE = 64; // downsample for speed
      const tmp = document.createElement("canvas");
      tmp.width = SIZE;
      tmp.height = SIZE;
      const ctx = tmp.getContext("2d");
      ctx.drawImage(imgEl, 0, 0, SIZE, SIZE);
      const data = ctx.getImageData(0, 0, SIZE, SIZE).data;
      const counts = {};
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        if (a < 128) continue; // skip transparent
        // Quantise to reduce noise — bucket to nearest 16
        const rq = Math.round(r / 16) * 16;
        const gq = Math.round(g / 16) * 16;
        const bq = Math.round(b / 16) * 16;
        // Skip near-white (boring) and near-black (often shadows)
        const brightness = (rq + gq + bq) / 3;
        if (brightness > 220 || brightness < 20) continue;
        const key = `${rq},${gq},${bq}`;
        counts[key] = (counts[key] || 0) + 1;
      }
      const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      if (!best) return null;
      const [r, g, b] = best[0].split(",").map(Number);
      return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
    } catch (_) {
      return null;
    }
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fabric = window.fabric;
    const c = fabricRef.current;
    if (!c) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      fabric.Image.fromURL(ev.target.result, (img) => {
        const dims = getPanelDimsInches(panel, setup);
        const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
        const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
        // Default behaviour: snap the uploaded image to fill the entire
        // bleed-to-bleed area. Authors almost always upload an image to
        // BE the cover, not as a small inset element. The user can
        // afterwards scale it down by dragging the corners or hitting
        // "Fit inside trim" in the properties panel.
        //
        // Previous behaviour used a 15%-aspect-ratio heuristic to pick
        // between full-bleed and an 85%×70% inset, which left aspect-
        // mismatched images sitting tiny in the centre. The user feedback
        // ("imported image is tiny") was a direct consequence of that
        // heuristic.
        const scale = Math.max(widthPx / img.width, heightPx / img.height);
        img.set({
          left: widthPx / 2,
          top: heightPx / 2,
          originX: "center",
          originY: "center",
          scaleX: scale,
          scaleY: scale,
          name: "image",
        });
        c.add(img);
        // Push to back so subsequent text/shape layers sit on top.
        c.sendToBack(img);
        const overlay = c.getObjects().find((o) => o.name === "_overlay");
        if (overlay) c.bringForward(overlay);
        guidesRef.current.forEach((g) => c.bringToFront(g));
        c.setActiveObject(img);
        activeObjRef.current = img;
        setActiveProps(snapshotProps(img));

        // Auto-fill background with the dominant colour from the uploaded image.
        // This gives instant visual harmony — spine & back bleed areas match
        // the cover art automatically. User can still override via the colour picker.
        const dominant = getDominantColour(img.getElement());
        if (dominant) {
          setBgColor(dominant);
          pushRecentColor(dominant);
        }

        c.requestRenderAll();
      });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  /* "Fit to bleed" — fills the ENTIRE bleed area (image edges hide under
     the trim cut). Best for full-bleed cover images. */
  const fitActiveImageToBleed = () => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!c || !obj || obj.type !== "image") return;
    const dims = getPanelDimsInches(panel, setup);
    const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
    const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
    const baseW = obj.width || 1;
    const baseH = obj.height || 1;
    const scale = Math.max(widthPx / baseW, heightPx / baseH);
    obj.set({
      left: widthPx / 2,
      top: heightPx / 2,
      originX: "center",
      originY: "center",
      scaleX: scale,
      scaleY: scale,
      angle: 0,
    });
    obj.setCoords();
    c.sendToBack(obj);
    const overlay = c.getObjects().find((o) => o.name === "_overlay");
    if (overlay) c.bringForward(overlay);
    guidesRef.current.forEach((g) => c.bringToFront(g));
    c.requestRenderAll();
    setActiveProps(snapshotProps(obj));
    dirtyRef.current = true;
  };

  /* "Fit inside trim" — fits the image entirely within the trim area
     (no crop). Best when the image has important content at the edges
     that shouldn't be cut. */
  const fitActiveImageInsideTrim = () => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!c || !obj || obj.type !== "image") return;
    const dims = getPanelDimsInches(panel, setup);
    const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
    const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
    const trimW = dims.trimW * DISPLAY_SCALE;
    const trimH = dims.trimH * DISPLAY_SCALE;
    const baseW = obj.width || 1;
    const baseH = obj.height || 1;
    const scale = Math.min(trimW / baseW, trimH / baseH);
    obj.set({
      left: widthPx / 2,
      top: heightPx / 2,
      originX: "center",
      originY: "center",
      scaleX: scale,
      scaleY: scale,
      angle: 0,
    });
    obj.setCoords();
    c.requestRenderAll();
    setActiveProps(snapshotProps(obj));
    dirtyRef.current = true;
  };

  /* "Center on canvas" — just re-centers the active object without
     changing its scale. Useful for any layer (image, text, shape). */
  const centerActiveOnCanvas = () => {
    const c = fabricRef.current;
    const obj = activeObjRef.current;
    if (!c || !obj) return;
    const dims = getPanelDimsInches(panel, setup);
    const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
    const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
    obj.set({
      left: widthPx / 2,
      top: heightPx / 2,
      originX: "center",
      originY: "center",
    });
    obj.setCoords();
    c.requestRenderAll();
    setActiveProps(snapshotProps(obj));
    dirtyRef.current = true;
  };

  const placeAIImage = (dataUrl) => {
    const fabric = window.fabric;
    const c = fabricRef.current;
    if (!c) return;
    fabric.Image.fromURL(dataUrl, (img) => {
      const dims = getPanelDimsInches(panel, setup);
      const widthPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
      const heightPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
      const scale = Math.max(widthPx / img.width, heightPx / img.height);
      img.set({ left: widthPx / 2, top: heightPx / 2, originX: "center", originY: "center", scaleX: scale, scaleY: scale, name: "ai-image" });
      c.add(img);
      c.sendToBack(img);
      // Make sure overlay stays above the background image.
      const overlay = c.getObjects().find((o) => o.name === "_overlay");
      if (overlay) c.bringForward(overlay);
      guidesRef.current.forEach((g) => c.bringToFront(g));
      c.setActiveObject(img);
      activeObjRef.current = img;
      setActiveProps(snapshotProps(img));
      c.requestRenderAll();
    });
    setShowAI(false);
  };

  /* --------------------------- AI edit (FLUX Kontext) --------------------------- */
  // Stamp a stable id on the layer the first time it's edited so we can
  // key its session history.
  const ensureEditId = (obj) => {
    if (!obj.__pkEditId) {
      obj.__pkEditId = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `eid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
    return obj.__pkEditId;
  };

  const replaceImageSrc = (obj, newSrc) => {
    const c = fabricRef.current;
    const fabric = window.fabric;
    if (!c || !obj || !fabric) return;
    // Preserve geometry by re-using the layer's existing transform.
    const keep = {
      left: obj.left, top: obj.top,
      originX: obj.originX, originY: obj.originY,
      angle: obj.angle || 0,
      opacity: obj.opacity != null ? obj.opacity : 1,
      name: obj.name || "ai-image",
      __pkEditId: obj.__pkEditId,
    };
    // Re-snap the new image to occupy the same on-canvas footprint as the
    // old one (same final width/height in pixels).
    const targetW = (obj.width || 1) * (obj.scaleX || 1);
    const targetH = (obj.height || 1) * (obj.scaleY || 1);
    fabric.Image.fromURL(newSrc, (img) => {
      const scaleX = targetW / (img.width || 1);
      const scaleY = targetH / (img.height || 1);
      img.set({ ...keep, scaleX, scaleY });
      // Replace in place — preserve stacking order.
      const stackIndex = c.getObjects().indexOf(obj);
      c.remove(obj);
      c.insertAt(img, stackIndex, false);
      c.setActiveObject(img);
      activeObjRef.current = img;
      setActiveProps(snapshotProps(img));
      guidesRef.current.forEach((g) => c.bringToFront(g));
      c.requestRenderAll();
      dirtyRef.current = true;
    }, { crossOrigin: "anonymous" });
  };

  const handleEditAIAccept = (newSrc, prompt) => {
    const obj = activeObjRef.current;
    if (!obj || obj.type !== "image") return;
    const id = ensureEditId(obj);
    const prevSrc = obj.getSrc ? obj.getSrc() : obj._element?.src;
    const list = editHistory.current[id] || [];
    // Push the pre-edit state so the user can revert to it.
    editHistory.current[id] = [
      { src: prevSrc, prompt, ts: Date.now() },
      ...list,
    ].slice(0, 5);
    setHistoryVersion((v) => v + 1);
    replaceImageSrc(obj, newSrc);
    setShowAIEdit(false);
  };

  const handleEditAIRevert = (entry) => {
    const obj = activeObjRef.current;
    if (!obj || obj.type !== "image" || !entry?.src) return;
    replaceImageSrc(obj, entry.src);
  };

  const activeIsImage = !!(activeProps && activeProps.type === "image");
  const activeImageSrc = activeIsImage
    ? (activeObjRef.current?.getSrc ? activeObjRef.current.getSrc() : activeObjRef.current?._element?.src)
    : null;
  const activeImageId = activeIsImage && activeObjRef.current?.__pkEditId;
  // historyVersion is referenced so React re-renders the panel when the
  // history ref mutates (refs don't trigger renders on their own).
  void historyVersion;
  const activeImageHistory = activeImageId ? (editHistory.current[activeImageId] || []) : [];

  /* --------------------------- Layers actions --------------------------- */
  const moveLayer = (idx, dir) => {
    const c = fabricRef.current;
    if (!c) return;
    const objs = c.getObjects().filter((o) => !isInternalObj(o));
    const obj = objs[idx];
    if (!obj) return;
    if (dir === "up") c.bringForward(obj);
    else c.sendBackwards(obj);
    guidesRef.current.forEach((g) => c.bringToFront(g));
    refreshLayers();
    c.requestRenderAll();
  };
  const toggleVisible = (idx) => {
    const c = fabricRef.current;
    const obj = c.getObjects().filter((o) => !isInternalObj(o))[idx];
    obj.set({ visible: !obj.visible });
    refreshLayers();
    c.requestRenderAll();
  };
  const toggleLock = (idx) => {
    const c = fabricRef.current;
    const obj = c.getObjects().filter((o) => !isInternalObj(o))[idx];
    const lock = !obj.lockMovementX;
    obj.set({ lockMovementX: lock, lockMovementY: lock, lockScalingX: lock, lockScalingY: lock, lockRotation: lock });
    refreshLayers();
    c.requestRenderAll();
  };
  const removeLayer = (idx) => {
    const c = fabricRef.current;
    const obj = c.getObjects().filter((o) => !isInternalObj(o))[idx];
    c.remove(obj);
    if (activeObjRef.current === obj) { activeObjRef.current = null; setActiveProps(null); }
    refreshLayers();
    c.requestRenderAll();
  };
  const selectLayer = (obj) => {
    const c = fabricRef.current;
    c.setActiveObject(obj);
    c.requestRenderAll();
    activeObjRef.current = obj;
    setActiveProps(snapshotProps(obj));
  };

  /* --------------------------- Save --------------------------- */
  const collectAllPanels = () => {
    if (fabricRef.current) {
      panelStates.current[panel] = fabricRef.current.toJSON([
        "excludeFromExport", "lockMovementX", "lockMovementY", "lockScalingX", "lockScalingY",
        "selectable", "name", "hidden",
      ]);
    }
    return panelStates.current;
  };

  const handleSave = async (opts = {}) => {
    if (saving) return;
    if (opts.silent && !dirtyRef.current) return;
    setSaving(true);
    try {
      const states = collectAllPanels();
      const thumb = fabricRef.current?.toDataURL({ format: "png", multiplier: 0.4 }) || "";
      const dims = opts.overrideSetup || {};
      const payload = {
        id: savedIdRef.current,
        book_title: setup.book_title,
        subtitle: setup.subtitle || "",
        author: setup.author,
        trim_size: dims.trim_size ?? setup.trim_size,
        page_count: dims.page_count ?? setup.page_count,
        paper_type: dims.paper_type ?? setup.paper_type,
        spine_width_inches: dims.spine_width_inches ?? setup.spine_width_inches,
        binding_type: dims.binding_type ?? setup.binding_type ?? "paperback",
        front_canvas_json: states.front,
        spine_canvas_json: states.spine,
        back_canvas_json: states.back,
        thumbnail_url: thumb,
      };
      const { data } = await api.post("/cover/save", payload);
      if (data?.id) {
        const isFirst = !savedIdRef.current;
        savedIdRef.current = data.id;
        setSavedId(data.id);
        setLastSavedAt(new Date());
        dirtyRef.current = false;
        if (isFirst) navigate(`/cover/${data.id}`, { replace: true });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "limit_reached") {
        setLimitInfo({ type: detail.type || "cover", plan: detail.plan || profile?.plan || "free" });
      } else if (!opts.silent) {
        alert("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!fabricReady) return;
    // Tight 5s autosave when dirty. Was 30s — long enough for users to
    // lose work if they swipe-back or close the tab before the timer
    // fires. 5s gives near-realtime safety without spamming the server.
    const id = setInterval(() => {
      if (dirtyRef.current && handleSaveRef.current) handleSaveRef.current({ silent: true });
    }, 5000);
    return () => clearInterval(id);
  }, [fabricReady]);

  // Fire a best-effort save when the editor unmounts (e.g. user clicks
  // browser back, which is an SPA route change and does NOT trigger
  // beforeunload). The save runs synchronously enough that the request
  // is in-flight by the time the route is gone; axios keeps the
  // connection alive on the existing origin.
  useEffect(() => {
    return () => {
      if (dirtyRef.current && handleSaveRef.current) {
        try { handleSaveRef.current({ silent: true }); } catch (_) {}
      }
    };
  }, []);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current) {
        try { handleSaveRef.current?.({ silent: true }); } catch (_) {}
        e.preventDefault();
        e.returnValue = "Unsaved changes — leave anyway?";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Intercept SPA back-navigation (browser back button, mac swipe-back)
  // and confirm with the user when there are unsaved changes. Without
  // this, a back press silently dumps the work-in-progress. We push a
  // sentinel state into history so the first back goes here instead of
  // unmounting the editor; if the user confirms, we go back twice.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.history.pushState({ pkEditor: true }, "");
    const onPop = (e) => {
      if (!dirtyRef.current) return; // nothing to lose; let it through
      const ok = window.confirm(
        "You have unsaved changes on this cover. Leave the editor now? Your latest changes may not be saved."
      );
      if (ok) {
        // User confirmed leave — try one last save then actually go back.
        try { handleSaveRef.current?.({ silent: true }); } catch (_) {}
        window.history.back();
      } else {
        // Re-push the sentinel so subsequent back presses trigger us again.
        window.history.pushState({ pkEditor: true }, "");
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /* --------------------------- Export --------------------------- */
  const renderPanelHighRes = (panelKey, states, multiplier = 5) => new Promise((resolve) => {
    const fabric = window.fabric;
    const trim = TRIM_SIZES.find((t) => t.value === setup.trim_size) || TRIM_SIZES[0];
    const dims = panelKey === "spine"
      ? { trimW: setup.spine_width_inches || 0.25, trimH: trim.h }
      : { trimW: trim.w, trimH: trim.h };
    const baseW = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
    const baseH = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
    const el = document.createElement("canvas");
    el.width = Math.round(baseW);
    el.height = Math.round(baseH);
    const c = new fabric.StaticCanvas(el, { width: el.width, height: el.height });
    const json = states[panelKey];
    if (!json) {
      c.backgroundColor = "#FFFFFF";
      c.renderAll();
      const url = c.toDataURL({ format: "png", multiplier });
      c.dispose();
      resolve({ url, baseW, baseH });
      return;
    }
    c.loadFromJSON(json, () => {
      c.getObjects().filter((o) => (o.name || "").startsWith("_guide")).forEach((g) => c.remove(g));
      c.renderAll();
      const url = c.toDataURL({ format: "png", multiplier });
      c.dispose();
      resolve({ url, baseW, baseH });
    });
  });

  const loadImg = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  const safeFileBase = () => (setup.book_title || "cover").replace(/[^a-zA-Z0-9-_]/g, "-").toLowerCase();

  const applyWatermark = (dataUrl) => new Promise(async (resolve) => {
    const img = await loadImg(dataUrl);
    const tmp = document.createElement("canvas");
    tmp.width = img.width;
    tmp.height = img.height;
    const ctx = tmp.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const fontSize = Math.max(18, Math.round(img.width / 50));
    ctx.font = `500 ${fontSize}px "Outfit", "Inter", sans-serif`;
    ctx.textBaseline = "bottom";
    ctx.textAlign = "right";
    const padX = Math.round(fontSize * 0.9);
    const padY = Math.round(fontSize * 0.9);
    const text = "Created with PublisherKit";
    ctx.lineWidth = Math.max(2, fontSize * 0.12);
    ctx.strokeStyle = "rgba(0,0,0,0.45)";
    ctx.strokeText(text, img.width - padX, img.height - padY);
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.fillText(text, img.width - padX, img.height - padY);
    resolve(tmp.toDataURL("image/png"));
  });

  const exportFront = async (format = "png") => {
    if (exporting) return;
    setExporting(true);
    try {
      // Always export the FRONT panel regardless of which tab the user is
      // currently viewing. Previously this used `fabricRef.current` which
      // is whatever panel is active in the editor, so clicking "Export
      // front cover" while on the spine tab exported the spine.
      const states = collectAllPanels();
      const MULT = 5;
      const { url } = await renderPanelHighRes("front", states, MULT);
      let dataUrl = url;
      if (profile?.plan === "free") dataUrl = await applyWatermark(dataUrl);
      if (format === "jpeg") {
        // Re-encode the rendered (and possibly watermarked) PNG as JPEG so
        // the .jpg file actually contains JPEG bytes.
        const img = await loadImg(dataUrl);
        const tmp = document.createElement("canvas");
        tmp.width = img.width;
        tmp.height = img.height;
        const ctx = tmp.getContext("2d");
        // JPEG has no transparency — paint white first.
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, tmp.width, tmp.height);
        ctx.drawImage(img, 0, 0);
        dataUrl = tmp.toDataURL("image/jpeg", 0.95);
      }
      const link = document.createElement("a");
      link.download = `${safeFileBase()}-front-cover.${format === "jpeg" ? "jpg" : "png"}`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Front cover export failed", err);
      alert("Front cover export failed: " + (err?.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  };

  const buildFullWrap = async () => {
    const states = collectAllPanels();
    const trim = TRIM_SIZES.find((t) => t.value === setup.trim_size) || TRIM_SIZES[0];
    const trimW = trim.w;
    const trimH = trim.h;
    // When there's no printed spine, KDP wraps the cover at the edge. Use
    // a zero-width spine so the stitched image is just back+front, no gap.
    const spineW = hasSpine ? (setup.spine_width_inches || 0.25) : 0;
    const MULT = 5;
    const PX_PER_INCH = DISPLAY_SCALE * MULT;
    const panelRenders = [renderPanelHighRes("back", states, MULT), renderPanelHighRes("front", states, MULT)];
    if (hasSpine) panelRenders.splice(1, 0, renderPanelHighRes("spine", states, MULT));
    const renders = await Promise.all(panelRenders);
    const back = renders[0];
    const spine = hasSpine ? renders[1] : null;
    const front = hasSpine ? renders[2] : renders[1];
    const totalWidthInches = trimW * 2 + spineW + BLEED * 2;
    const totalHeightInches = trimH + BLEED * 2;
    const stitch = document.createElement("canvas");
    stitch.width = Math.round(totalWidthInches * PX_PER_INCH);
    stitch.height = Math.round(totalHeightInches * PX_PER_INCH);
    const ctx = stitch.getContext("2d");
    // Paint a white floor so even spineless covers have no transparency
    // (transparency in the embedded image can trigger KDP rejections).
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, stitch.width, stitch.height);
    const imgs = await Promise.all(
      [back.url, spine?.url, front.url].filter(Boolean).map(loadImg)
    );
    const [backImg, spineImg, frontImg] = hasSpine
      ? [imgs[0], imgs[1], imgs[2]]
      : [imgs[0], null, imgs[1]];
    ctx.drawImage(backImg, 0, 0);
    if (spineImg) ctx.drawImage(spineImg, Math.round(trimW * PX_PER_INCH), 0);
    ctx.drawImage(frontImg, Math.round((trimW + spineW) * PX_PER_INCH), 0);
    let url = stitch.toDataURL("image/png");
    if (profile?.plan === "free") url = await applyWatermark(url);
    return { url, totalWidthInches, totalHeightInches };
  };

  const exportFullWrapPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { url, totalWidthInches, totalHeightInches } = await buildFullWrap();
      // KDP prefers a fully-flat image with no transparency. Re-encode the
      // stitched canvas as a JPEG (no alpha channel) before embedding in
      // the PDF; this also keeps file sizes well below KDP's 650 MB limit.
      const img = await loadImg(url);
      const tmp = document.createElement("canvas");
      tmp.width = img.width;
      tmp.height = img.height;
      const ctx = tmp.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, tmp.width, tmp.height);
      ctx.drawImage(img, 0, 0);
      const jpegUrl = tmp.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({
        orientation: totalWidthInches >= totalHeightInches ? "landscape" : "portrait",
        unit: "in",
        format: [totalWidthInches, totalHeightInches],
        compress: false, // KDP-friendly: avoid Flate-compressed image streams
      });
      pdf.addImage(jpegUrl, "JPEG", 0, 0, totalWidthInches, totalHeightInches, undefined, "NONE");
      pdf.save(`${safeFileBase()}-full-wrap-cover.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
      alert("PDF export failed: " + (err?.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  };
  const exportFullWrapPNG = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { url } = await buildFullWrap();
      const link = document.createElement("a");
      link.download = `${safeFileBase()}-full-wrap-cover.png`;
      link.href = url;
      link.click();
    } catch (err) {
      console.error("PNG export failed", err);
      alert("PNG export failed: " + (err?.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  };

  /* --------------------------- Series palette apply --------------------------- */
  const applyPalette = (p) => {
    if (p?.bg) setBgColor(p.bg);
    (p.accents || []).forEach((c) => pushRecentColor(c));
  };

  if (!fabricReady) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F1EFE8]">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#534AB7] mx-auto" />
          <p className="mt-3 text-sm text-[#5F5E5A]">Loading canvas engine…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full grid" style={{ gridTemplateRows: "56px 1fr" }} data-testid="cover-editor">
      <EditorTopBar
        setup={setup}
        panel={panel}
        onSelectPanel={setPanel}
        hasSpine={hasSpine}
        saving={saving}
        lastSavedAt={lastSavedAt}
        foundLostCount={foundLostCount}
        onFindLost={findLostObjects}
        onUndo={undo}
        onRedo={redo}
        onAssemblePreview={() => { collectAllPanels(); setShowPreview(true); }}
        onExportFullPDF={exportFullWrapPDF}
        onExportFullPNG={exportFullWrapPNG}
        onExportFront={exportFront}
        exporting={exporting}
        onSave={handleSave}
        savedId={savedId}
        onBack={() => navigate("/dashboard")}
        isFreePlan={profile?.plan === "free"}
        onShare={() => setShowShare(true)}
        onResize={onResize ? () => setShowResize(true) : null}
        showTemplate={showTemplate}
        onToggleTemplate={() => setShowTemplate((v) => !v)}
      />

      <div className="grid h-full overflow-hidden" style={{ gridTemplateColumns: "260px 1fr 280px" }}>
        <LeftToolbar
          panel={panel}
          bgColor={bgColor}
          setBgColor={setBgColor}
          panelBg={panelBg}
          spineMatchFront={spineMatchFront}
          setSpineMatchFront={setSpineMatchFront}
          onSampleSpineFromFront={sampleFrontDominantColor}
          sampling={sampling}
          eyedropping={eyedropping}
          onToggleEyedropper={() => setEyedropping((v) => !v)}
          backOverlay={backOverlay}
          setBackOverlay={setBackOverlay}
          onApplyPalette={applyPalette}
          onPlaceBlurb={placeBlurb}
          onOpenBlurbModal={() => setShowBlurb(true)}
          onUpload={handleUpload}
          onOpenAI={() => setShowAI(true)}
          onAddText={addText}
          onAddShape={addShape}
          layers={layers}
          activeObj={activeObjRef.current}
          onSelectLayer={selectLayer}
          onMoveLayer={moveLayer}
          onToggleVisible={toggleVisible}
          onToggleLock={toggleLock}
          onRemoveLayer={removeLayer}
        />

        <div className="bg-[#E8E5DA] flex items-center justify-center overflow-auto p-8" data-testid="canvas-area">
          <div className="bg-white shadow-[0_30px_70px_-20px_rgba(0,0,0,0.25)]">
            <canvas ref={canvasElRef} />
          </div>
        </div>

        <PropertiesPanel
          activeProps={activeProps}
          onUpdate={updateActive}
          onUpdateSize={updateActiveSize}
          onUpdateRadius={updateActiveRadius}
          onDuplicate={duplicateActive}
          onDelete={deleteActive}
          onBringToFront={bringToFront}
          onSendToBack={sendToBack}
          onFitToBleed={fitActiveImageToBleed}
          onFitInsideTrim={fitActiveImageInsideTrim}
          onCenter={centerActiveOnCanvas}
          onEditAI={() => setShowAIEdit(true)}
        />
      </div>

      {showAI && (
        <AIImagePanel
          onClose={() => setShowAI(false)}
          onAccept={placeAIImage}
          onUpload={() => {
            const i = document.createElement("input");
            i.type = "file";
            i.accept = "image/*";
            i.onchange = (e) => handleUpload(e);
            i.click();
            setShowAI(false);
          }}
        />
      )}

      <AIEditPanel
        open={showAIEdit}
        onClose={() => setShowAIEdit(false)}
        currentSrc={activeImageSrc}
        history={activeImageHistory}
        coverProjectId={savedId}
        onAccept={handleEditAIAccept}
        onRevert={handleEditAIRevert}
      />

      <BlurbImportModal open={showBlurb} onClose={() => setShowBlurb(false)} onPlace={placeBlurb} />

      <ShareModal
        open={showShare}
        onClose={() => setShowShare(false)}
        projectId={savedId}
        initialToken={shareToken}
        onTokenChange={setShareToken}
        onSaveFirst={async () => {
          await handleSave();
        }}
      />

      <AssemblePreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        setup={setup}
        panelStates={panelStates.current}
        onJumpToPanel={(p) => { setShowPreview(false); setPanel(p); }}
        onExportPDF={async () => { setShowPreview(false); await exportFullWrapPDF(); }}
        exporting={exporting}
      />

      <ResizeKDPModal
        open={showResize}
        onClose={() => setShowResize(false)}
        current={setup}
        onConfirm={async (next) => {
          // Defensive: any error in this flow must NOT bubble up to the
          // React error boundary (which kills the editor). Catch, log,
          // toast.
          try {
            collectAllPanels();
          } catch (err) {
            console.warn("collectAllPanels failed during resize", err);
          }
          try {
            await handleSaveRef.current?.({ silent: false, overrideSetup: next });
          } catch (err) {
            console.error("Save failed during resize:", err);
            // Don't abort — still allow the resize to apply locally so
            // the user's work isn't blocked by a transient save error.
            try { alert("Couldn't save before resizing — your dimensions were updated but the project may not have been saved. Try clicking Save once the editor reopens."); } catch (_) {}
          }
          setShowResize(false);
          try {
            onResize?.(next);
          } catch (err) {
            console.error("Resize callback failed:", err);
          }
        }}
      />

      <LimitReachedModal
        open={!!limitInfo}
        type={limitInfo?.type || "cover"}
        plan={limitInfo?.plan || profile?.plan || "free"}
        onClose={() => setLimitInfo(null)}
      />
    </div>
  );
};

export default CoverEditor;
